<template>
  <Modal :model-value="modelValue" @update:model-value="emit('update:modelValue', $event)">
    <div class="flex justify-end">
      <button
        class="rounded-md p-1 transition hover:bg-obsidian-100"
        @click="emit('update:modelValue', false)"
      >
        <XMarkIcon class="size-6 text-red-500" />
      </button>
    </div>
    <div class="grid gap-4 md:grid-cols-2 md:items-start">
      <div class="grid gap-4">
        <LabeledText :label="t('created')">{{ createdDate }}</LabeledText>
        <div v-if="!isInEditMode" class="flex items-start justify-between gap-2">
          <LabeledText :label="t('description')">{{ damage.description }}</LabeledText>
        </div>
        <div v-else class="space-y-4">
          <Input v-model="description" :label="t('description')" />
        </div>
        <LabeledText :label="t('repair')">
          <CVButton
            v-if="damage.status === DamageStatus.OPEN"
            class="justify-self-start"
            :is-loading="updatingDamage"
            @click.prevent="setDamageStatusToRepaired"
            >{{ t('confirmRepair') }}</CVButton
          >
          <span v-else>{{ t('repairedAt') }} {{ repairedDate }}</span>
        </LabeledText>
      </div>
      <div class="flex flex-col gap-4">
        <MarkerContainer
          v-if="hasPosition"
          :marker-positions="[position]"
          hide-position-numbers
          @select-position="isInEditMode && (position = $event)"
        />
        <div v-if="!isInEditMode" class="flex justify-end">
          <CVButton variant="link" @click="isInEditMode = true">
            <span>{{ t('editDamage') }}</span
            ><PencilIcon class="size-5" />
          </CVButton>
        </div>
        <div v-if="isInEditMode" class="flex justify-end gap-2">
          <CVButton variant="default" :is-loading="updatingDamage" @click="saveDamage">{{
            t('save')
          }}</CVButton>
          <CVButton variant="ghost" :disabled="updatingDamage" @click="cancelEdit">{{
            t('cancel')
          }}</CVButton>
        </div>
      </div>
      <div class="w-full md:col-span-2">
        <div v-if="damage?.videoFileUrl" class="mb-2 flex gap-2">
          <a
            href="#"
            :class="{ 'text-link': showVideo, 'text-gray-500': !showVideo }"
            @click.prevent="showVideo = false"
            >{{ t('picture') }}</a
          >
          <div class="text-primary">|</div>
          <a
            href="#"
            :class="{ 'text-link': !showVideo, 'text-gray-500': showVideo }"
            @click.prevent="showVideo = true"
            >{{ t('video') }}</a
          >
        </div>
        <div
          v-if="!showVideo && (damage.imageFileUrls?.length ?? 0) > 1"
          class="mb-2 flex flex-wrap gap-2"
        >
          <button
            v-for="(url, idx) in damage.imageFileUrls"
            :key="url"
            type="button"
            class="cursor-pointer"
            @click="selectedPhotoIndex = idx"
          >
            <img
              :src="url"
              :alt="t('picture')"
              class="h-12 w-12 rounded-md object-cover"
              :class="idx === selectedPhotoIndex ? 'ring-2 ring-hazel-500' : ''"
            />
          </button>
        </div>
        <ZoomableImage v-show="!showVideo" :src="activeImageUrl" img-class="w-full" />
        <video v-if="damage?.videoFileUrl" v-show="showVideo" class="w-full" controls>
          <source :src="damage?.videoFileUrl" />
        </video>
        <p v-if="isPendingEvidence" class="text-amber-800 mt-2 text-sm">
          {{ t('pendingEvidence') }}
        </p>
        <div v-if="useInAppEvidence" class="mt-4 space-y-2">
          <StatusBanner v-if="isAddCameraUnavailable" variant="warning">
            {{ t('cameraUnavailable') }}
          </StatusBanner>
          <CVButton
            v-if="isMobile && !isAddCameraUnavailable"
            type="button"
            variant="secondary"
            @click="handleOpenAddCamera"
          >
            {{ t('addPhoto') }}
          </CVButton>
          <input
            ref="addPhotoInput"
            type="file"
            accept="image/*"
            multiple
            class="hidden"
            @change="handleAddPhotoFiles"
          />
          <CVButton
            v-if="!isMobile || isAddCameraUnavailable"
            type="button"
            variant="ghost"
            @click="addPhotoInput?.click()"
          >
            {{ t('addFromFiles') }}
          </CVButton>
          <DamageEvidenceCamera
            v-if="showAddCamera"
            @captured="handleAddedPhoto"
            @close="showAddCamera = false"
            @failed="handleAddCameraFailed"
          />
        </div>
      </div>
    </div>
  </Modal>
</template>

<script lang="ts" setup>
import LabeledText from '@/components/LabeledText.vue';
import StatusBanner from '@/components/StatusBanner.vue';
import type { Damage, Position } from '@/entities/damage.entity';
import { useFormattedDate } from '@/hooks/use-formatted-date';
import { useI18n } from 'vue-i18n';
import { computed, ref, watch } from 'vue';
import MarkerContainer from './components/MarkerContainer.vue';
import { DamageStatus } from '@carvia/ros-client-types';
import { useUpdateDamage } from '@/queries/use-damages';
import { Alert } from '@/services/alert.service';
import Input from '@/components/Input.vue';
import CVButton from '@/components/buttons/CVButton.vue';
import { PencilIcon, XMarkIcon } from '@heroicons/vue/24/outline';
import Modal from '@/components/Modal.vue';
import ZoomableImage from '@/components/ZoomableImage.vue';
import DamageEvidenceCamera from './DamageEvidenceCamera.vue';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { compressImageToJpeg } from '@/utils/compress-jpeg';
import {
  DAMAGE_IN_APP_EVIDENCE_FLAG,
  hasPendingDamageEvidence,
  isTouchCaptureDevice,
} from '@/utils/damage-evidence';
import { evidenceUploadQueue } from '@/utils/evidence-upload-queue';

const props = defineProps<{
  modelValue: boolean;
  damage: Damage;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
}>();

const { t } = useI18n();
const useInAppEvidence = useFeatureFlag(DAMAGE_IN_APP_EVIDENCE_FLAG);
const showAddCamera = ref(false);
const addPhotoInput = ref<HTMLInputElement | null>(null);
/** On phone and tablet photos come from the camera; files only if it cannot start. */
const isMobile = isTouchCaptureDevice();
const isAddCameraUnavailable = ref(false);
const isPendingEvidence = computed(() => hasPendingDamageEvidence(props.damage));

const description = ref(props.damage.description);
const position = ref<Position>({
  positionX: props.damage.positionX ?? undefined,
  positionY: props.damage.positionY ?? undefined,
});
const hasPosition = computed(
  () => props.damage.positionX != null && props.damage.positionY != null,
);

const isInEditMode = ref(false);

const { mutateAsync: updateDamage, isPending: updatingDamage } = useUpdateDamage();
const showVideo = ref(false);
const selectedPhotoIndex = ref(0);

const activeImageUrl = computed(
  () =>
    props.damage.imageFileUrls?.[selectedPhotoIndex.value] ?? props.damage.imageFileUrls?.[0] ?? '',
);

watch(
  () => props.damage.id,
  () => {
    selectedPhotoIndex.value = 0;
  },
);

const createdDate = useFormattedDate(computed(() => props.damage.createdDate));

const repairedDate = useFormattedDate(computed(() => props.damage.repairedDate));

const setDamageStatusToRepaired = async () => {
  const result = await Alert.fire({
    title: t('areYouSure'),
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: t('yes'),
    cancelButtonText: t('no'),
  });

  if (!result.isConfirmed) return;

  await updateDamage({
    id: props.damage?.id,
    damage: {
      status: DamageStatus.REPAIRED,
    },
  });
};

const saveDamage = async () => {
  await updateDamage({
    id: props.damage?.id,
    damage: {
      description: description.value,
      ...(hasPosition.value
        ? {
            positionX: position.value.positionX,
            positionY: position.value.positionY,
          }
        : {}),
    },
  });
  isInEditMode.value = false;
};

/**
 * Photos added later only go into the upload queue. The expected count stays at the photos taken
 * when recording, so later additions never hold back the fleet mail.
 */
const enqueuePhotos = async (files: File[]) => {
  if (files.length === 0) return;
  const compressed = await Promise.all(files.map((file) => compressImageToJpeg(file)));
  await evidenceUploadQueue.enqueue(
    props.damage.id,
    props.damage.car.id,
    compressed.map((file) => ({ kind: 'photo' as const, file, name: file.name })),
  );
};

const handleOpenAddCamera = () => {
  showAddCamera.value = true;
};

const handleAddedPhoto = async (file: File) => {
  showAddCamera.value = false;
  await enqueuePhotos([file]);
};

const handleAddCameraFailed = () => {
  showAddCamera.value = false;
  isAddCameraUnavailable.value = true;
};

const handleAddPhotoFiles = async (event: Event) => {
  const input = event.target as HTMLInputElement;
  const files = input.files ? Array.from(input.files) : [];
  input.value = '';
  await enqueuePhotos(files);
};

const cancelEdit = async () => {
  isInEditMode.value = false;
  position.value = {
    positionX: props.damage.positionX ?? undefined,
    positionY: props.damage.positionY ?? undefined,
  };
  description.value = props.damage.description;
};
</script>

<i18n lang="json">
{
  "en": {
    "created": "Created",
    "description": "Description",
    "repair": "Repair",
    "confirmRepair": "Confirm repair",
    "repairedAt": "Repaired at",
    "areYouSure": "Are you sure?",
    "picture": "Picture",
    "video": "Video",
    "editDamage": "Edit damage",
    "pendingEvidence": "Waiting for evidence from the recording device.",
    "addPhoto": "Add photo",
    "addFromFiles": "Add from files",
    "cameraUnavailable": "The camera could not start. Please choose photos from your files."
  },
  "de": {
    "created": "Erstellt",
    "description": "Beschreibung",
    "repair": "Reparatur",
    "confirmRepair": "Reparatur bestätigen",
    "repairedAt": "Repariert am",
    "areYouSure": "Bist du sicher?",
    "picture": "Foto",
    "video": "Video",
    "editDamage": "Schaden bearbeiten",
    "pendingEvidence": "Warte auf Nachweise vom aufnehmenden Gerät.",
    "addPhoto": "Foto hinzufügen",
    "addFromFiles": "Aus Dateien",
    "cameraUnavailable": "Die Kamera konnte nicht gestartet werden. Bitte Fotos aus den Dateien wählen."
  },
  "es": {
    "created": "Creado",
    "description": "Descripción",
    "repair": "Reparación",
    "confirmRepair": "Confirmar reparación",
    "repairedAt": "Reparado el",
    "areYouSure": "¿Seguro?",
    "picture": "Foto",
    "video": "Vídeo",
    "editDamage": "Editar daño",
    "pendingEvidence": "Esperando pruebas del dispositivo que las grabó.",
    "addPhoto": "Añadir foto",
    "addFromFiles": "Desde archivos",
    "cameraUnavailable": "No se pudo iniciar la cámara. Elija las fotos desde sus archivos."
  }
}
</i18n>
